import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AppStateFailureBuilder, AppStateFailureItemWithErrorCode, FailureType } from '@symphony-solo/shared/common-state/src';
import { ApiResponseCode } from '@symphony-solo/shared/http/src';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AppConfigService, CompanyGroupService } from '../services';
import {
  getCompanyGroupAction,
  getCompanyGroupBySubDomainAction,
  getCompanyGroupBySubDomainSuccessAction,
  getCompanyGroupErrorAction,
  getCompanyGroupSuccessAction,
} from './company-group.actions';
import { AlertService } from '@symphony-solo/shared/alert/src';
import { ActivatedRoute } from '@angular/router';
import { NavController } from '@ionic/angular';
import { LOGIN, OTP } from '@symphony-solo/shared/utils/src';
import { CompanyGroupFacade } from './company-group.facade';

@Injectable()
export class CompanyGroupEffects {
  constructor(
    private action$: Actions,
    private companyGroupService: CompanyGroupService,
    private companyGroupFacade: CompanyGroupFacade,
    private alertService: AlertService,
    private appConfigService: AppConfigService,
    private route: ActivatedRoute,
    private navCtrl: NavController
  ) {}

  protected getCompanyGroup$ = createEffect(() =>
    this.action$.pipe(
      ofType(getCompanyGroupAction),
      withLatestFrom(this.companyGroupFacade.companyGroupInfo$),
      switchMap(([companyGroupRequest, companyGroupInfo]) => {
        const companyData = localStorage.getItem('COMPANY_DATA');
        let payload = companyGroupRequest.CompanyGroupRequest;
        let redirect = true;

        if (companyGroupInfo || companyData) {
          const company = companyData ? JSON.parse(companyData) : null;
          if(companyGroupInfo) {
            redirect = false;
          }
          payload = { groupGuid: 'D1590806-1938-482A-B88C-4BF0FD742677', appID: companyGroupInfo ? companyGroupInfo.appID : company.appID };
        }

        return this.companyGroupService.getCompanyGroup(payload).pipe(
          tap((company: any) => {
            this.appConfigService.setConfig(company);

            if (redirect) {
              let url = LOGIN;
              if (this.route.snapshot?.queryParams?.tta) {
                url = `${LOGIN}?tta=${this.route.snapshot?.queryParams?.tta}`;
              }
              this.navCtrl.navigateForward(url);
            }

            setTimeout(() => {
              const htmlEl = document.querySelector('html');
              htmlEl.style.setProperty('--ion-color-primary', company.primaryColour);
              htmlEl.style.setProperty('--ion-color-secondary', company.secondaryColour);
            }, 0);
          }),
          map((CompanyGroup: any) => getCompanyGroupSuccessAction({ CompanyGroup, redirect })),
          catchError((err) => {
            let errorMsg = 'Error on getting Company';
            if (companyGroupRequest.CompanyGroupRequest.appID === '' && err?.status === 404) {
              this.navCtrl.navigateForward('/otp?show=1');
            } else if (companyGroupRequest.CompanyGroupRequest.appID && err?.status === 404) {
              errorMsg = err?.error;
              const okButton = {
                text: 'OK',
                cssClass: 'okButton centerButton',
              };
              this.alertService.presentAlert(
                'Incorrect location code entered!',
                'Please check the code you were given has been entered correctly and try again.',
                [okButton]
              );
            }

            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode(errorMsg, ApiResponseCode.Server_Error))
              .build();
            return of(getCompanyGroupErrorAction({ failure }));
          })
        );
      })
    )
  );

  protected getCompanyGroupSuccess$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(getCompanyGroupSuccessAction),
        tap((action) => {
          if (action.redirect) {
            this.navCtrl.navigateForward(LOGIN);
          }
        })
      ),
    { dispatch: false }
  );

  protected getCompanyGroupBySubDomain$ = createEffect(() =>
    this.action$.pipe(
      ofType(getCompanyGroupBySubDomainAction),
      withLatestFrom(this.companyGroupFacade.companyGroupInfo$),
      switchMap(([payload, companyGroupInfo]) => {
        return this.companyGroupService.getCompanyGroupDomain(payload.subdomain).pipe(
          map((company: any) => {
            this.appConfigService.setConfig(company);
            return getCompanyGroupBySubDomainSuccessAction({ CompanyGroup: company, redirect: companyGroupInfo ? false : true });
          }),
          catchError((err) => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode(err?.error, ApiResponseCode.Server_Error))
              .build();
            return of(getCompanyGroupErrorAction({ failure }));
          })
        );
      })
    )
  );

  protected getCompanyGroupBySubDomainSuccess$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(getCompanyGroupBySubDomainSuccessAction),
        tap((action) => {
          if (action.redirect) {
            this.navCtrl.navigateForward(OTP);
          }
        })
      ),
    { dispatch: false }
  );
}
